import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import type CameraControlsImpl from 'camera-controls';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import * as THREE from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';

import type { Bone, Skeleton } from '../../../../../lib/skeleton/core';
import {
  buildGimbalQuaternions,
  composeMatrixFromTransform,
  decomposeMatrixToTransform,
  eulerDegreesFromQuaternion,
  quaternionFromEulerDegrees,
} from '../../../../../lib/skeleton/rotationService';
import { preferredAnglesAreSignificant } from '../../../../../presentations/properties';
import { ensurePreferredAngles, pruneBonePropertiesDefaults } from '../../../../Inspector/utils';
import { clampManipulatorScale, computeTransformControlsScale } from '../../shared';
import {
  AXIS_ARROW_ACTIVE_OPACITY,
  AXIS_ARROW_ANGLE_DEGREES,
  AXIS_ARROW_HEIGHT,
  AXIS_ARROW_IDLE_OPACITY,
  AXIS_ARROW_RADIAL_OFFSET,
  AXIS_ARROW_RADIUS,
  ACTIVE_RING_OPACITY,
  AXIS_COLOR_BY_KEY,
  AXIS_ORDER,
  GHOST_LINE_OPACITY,
  GHOST_LINE_WIDTH,
  GHOST_RENDER_ORDER,
  IDLE_RING_OPACITY,
  PICK_EDGE_ON_THRESHOLD,
  PICK_RADIAL_TOLERANCE,
  PREFERRED_ANGLE_EPSILON,
  RING_DASH_DEGREES,
  RING_DELTA_SWEEP_HALF_WIDTH,
  RING_DELTA_SWEEP_FILL_OPACITY,
  RING_DELTA_SWEEP_FILL_RADIUS,
  RING_DELTA_SWEEP_MIN_DEGREES,
  RING_DELTA_SWEEP_OPACITY,
  RING_DELTA_SWEEP_RADIUS,
  RING_GAP_DEGREES,
  RING_HALF_WIDTH,
  RING_HALF_WIDTH_SELECTED,
  RING_RADIUS,
  RING_SEGMENT_COUNT,
} from '../constants';
import type {
  DragAnchor,
  DragState,
  ManipulatorAxis,
  ManipulatorFrameState,
  PreferredAnglesAxisState,
} from '../types';
import {
  angleDegreesFromLocalPoint,
  appendAlternatingDashedSegment,
  axisPointFromDegrees,
  axisIndexFromKey,
  axisNormalVector,
  createAxisArcRibbonGeometry,
  createAxisSweepFillGeometry,
  createDashedAxisRingGeometry,
  normalizeDegrees,
  resolveGhostDashMetrics,
} from '../utils';

interface PreferredAnglesManipulatorProps {
  skeleton: Skeleton;
  selectedBoneIndex: number | null;
  enabled: boolean;
  manipulatorScale: number;
  poseVersion: number;
  propertiesVersion: number;
  onBonePropertiesChange?: () => void;
  onDraggingChange?: (isDragging: boolean) => void;
}

interface PreferredAnglesAxisRingProps {
  axis: ManipulatorAxis;
  axisQuaternion: THREE.Quaternion;
  isActive: boolean;
  deltaDegrees: number;
  sweepPlacement: AxisSweepPlacement | null;
  manipulatorStateRef: RefObject<ManipulatorFrameState>;
  onPointerDown: (
    axis: ManipulatorAxis,
    event: ThreeEvent<PointerEvent>,
  ) => void;
}

interface AnchorData {
  position: THREE.Vector3;
  gimbalQuats: Record<ManipulatorAxis, THREE.Quaternion>;
}

interface AxisSweepPlacement {
  startDegrees: number;
  endDegrees: number;
}

interface GhostLineObject {
  key: 'white' | 'black';
  geometry: LineSegmentsGeometry;
  material: LineMaterial;
  line: LineSegments2;
}

function readPreferredAnglesTriplet(bone: Bone): [number, number, number] {
  const preferredAngles = bone.properties?.preferredAngles;
  if (!preferredAngles) return [0, 0, 0];
  return [preferredAngles.x, preferredAngles.y, preferredAngles.z];
}

function AxisArrow({
  axis,
  color,
  isActive,
  angleDegrees,
}: {
  axis: ManipulatorAxis;
  color: number;
  isActive: boolean;
  angleDegrees: number;
}) {
  const arrowPosition = useMemo(
    () =>
      axisPointFromDegrees(
        axis,
        angleDegrees,
        RING_RADIUS + AXIS_ARROW_RADIAL_OFFSET,
      ),
    [angleDegrees, axis],
  );
  const arrowQuaternionTuple = useMemo<[number, number, number, number]>(() => {
    const radial = axisPointFromDegrees(axis, angleDegrees, 1).normalize();
    const tangent = axisNormalVector(axis).clone().cross(radial).normalize();
    const quaternion = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      tangent,
    );
    return [quaternion.x, quaternion.y, quaternion.z, quaternion.w];
  }, [angleDegrees, axis]);
  const arrowScale = isActive ? 1.15 : 1;

  return (
    <mesh
      position={[arrowPosition.x, arrowPosition.y, arrowPosition.z]}
      quaternion={arrowQuaternionTuple}
      scale={[arrowScale, arrowScale, arrowScale]}
      renderOrder={1255}
    >
      <coneGeometry args={[AXIS_ARROW_RADIUS, AXIS_ARROW_HEIGHT, 9, 1]} />
      <meshBasicMaterial
        color={color}
        transparent
        opacity={isActive ? AXIS_ARROW_ACTIVE_OPACITY : AXIS_ARROW_IDLE_OPACITY}
        depthTest={false}
        depthWrite={false}
        side={THREE.DoubleSide}
        toneMapped={false}
      />
    </mesh>
  );
}

function PreferredAnglesAxisRing({
  axis,
  axisQuaternion,
  isActive,
  deltaDegrees,
  sweepPlacement,
  manipulatorStateRef,
  onPointerDown,
}: PreferredAnglesAxisRingProps) {
  const axisQuaternionRef = useRef(axisQuaternion.clone());
  axisQuaternionRef.current.copy(axisQuaternion);

  const axisQuaternionTuple = useMemo<[number, number, number, number]>(
    () => [axisQuaternion.x, axisQuaternion.y, axisQuaternion.z, axisQuaternion.w],
    [axisQuaternion],
  );
  const ringHalfWidth = isActive ? RING_HALF_WIDTH_SELECTED : RING_HALF_WIDTH;
  const ringColor = AXIS_COLOR_BY_KEY[axis];
  const ringRotationDegrees = normalizeDegrees(deltaDegrees);

  const ringGeometry = useMemo(
    () =>
      createDashedAxisRingGeometry({
        axis,
        radius: RING_RADIUS,
        halfWidth: ringHalfWidth,
        segmentCount: RING_SEGMENT_COUNT,
        dashDegrees: RING_DASH_DEGREES,
        gapDegrees: RING_GAP_DEGREES,
        angleOffsetDegrees: ringRotationDegrees,
      }),
    [axis, ringHalfWidth, ringRotationDegrees],
  );

  const deltaSweepGeometry = useMemo(() => {
    if (!sweepPlacement) return null;
    const sweepSize = Math.abs(sweepPlacement.endDegrees - sweepPlacement.startDegrees);
    if (sweepSize < RING_DELTA_SWEEP_MIN_DEGREES) return null;
    return createAxisArcRibbonGeometry({
      axis,
      radius: RING_DELTA_SWEEP_RADIUS,
      halfWidth: RING_DELTA_SWEEP_HALF_WIDTH,
      segmentCount: RING_SEGMENT_COUNT,
      startDegrees: sweepPlacement.startDegrees,
      endDegrees: sweepPlacement.endDegrees,
    });
  }, [axis, sweepPlacement]);

  const deltaSweepFillGeometry = useMemo(() => {
    if (!sweepPlacement) return null;
    const sweepSize = Math.abs(sweepPlacement.endDegrees - sweepPlacement.startDegrees);
    if (sweepSize < RING_DELTA_SWEEP_MIN_DEGREES) return null;
    return createAxisSweepFillGeometry({
      axis,
      radius: RING_DELTA_SWEEP_FILL_RADIUS,
      segmentCount: RING_SEGMENT_COUNT,
      startDegrees: sweepPlacement.startDegrees,
      endDegrees: sweepPlacement.endDegrees,
    });
  }, [axis, sweepPlacement]);

  useEffect(
    () => () => {
      deltaSweepGeometry?.dispose();
    },
    [deltaSweepGeometry],
  );
  useEffect(
    () => () => {
      deltaSweepFillGeometry?.dispose();
    },
    [deltaSweepFillGeometry],
  );

  useEffect(
    () => () => {
      ringGeometry.dispose();
    },
    [ringGeometry],
  );

  const ringRaycast = useMemo(() => {
    const plane = new THREE.Plane();
    const hitPoint = new THREE.Vector3();
    const localPoint = new THREE.Vector3();
    const normal = new THREE.Vector3();
    const inverseQuaternion = new THREE.Quaternion();

    return function raycast(
      this: THREE.Object3D,
      raycaster: THREE.Raycaster,
      intersects: THREE.Intersection[],
    ) {
      const manipulatorState = manipulatorStateRef.current;
      if (!manipulatorState) return;

      const axisQuaternionValue = axisQuaternionRef.current;
      normal
        .copy(axisNormalVector(axis))
        .applyQuaternion(axisQuaternionValue)
        .normalize();

      const rayDirection = raycaster.ray.direction;
      const viewDot = Math.abs(
        rayDirection.x * normal.x +
          rayDirection.y * normal.y +
          rayDirection.z * normal.z,
      );
      if (viewDot > PICK_EDGE_ON_THRESHOLD) return;

      plane.setFromNormalAndCoplanarPoint(normal, manipulatorState.position);
      if (!raycaster.ray.intersectPlane(plane, hitPoint)) return;

      inverseQuaternion.copy(axisQuaternionValue).invert();
      localPoint
        .copy(hitPoint)
        .sub(manipulatorState.position)
        .applyQuaternion(inverseQuaternion)
        .divideScalar(Math.max(1e-5, manipulatorState.scale));

      const radialDistance = localPoint.length();
      if (Math.abs(radialDistance - RING_RADIUS) > PICK_RADIAL_TOLERANCE) return;

      intersects.push({
        distance: hitPoint.distanceTo(raycaster.ray.origin),
        point: hitPoint.clone(),
        object: this,
      } as THREE.Intersection);
    };
  }, [axis, manipulatorStateRef]);

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    onPointerDown(axis, event);
  };

  const ringOpacity = isActive ? ACTIVE_RING_OPACITY : IDLE_RING_OPACITY;

  return (
    <group quaternion={axisQuaternionTuple}>
      <mesh geometry={ringGeometry} raycast={ringRaycast} onPointerDown={handlePointerDown}>
        <meshBasicMaterial
          color={ringColor}
          transparent
          opacity={ringOpacity}
          depthTest={false}
          depthWrite={false}
          side={THREE.DoubleSide}
          toneMapped={false}
        />
      </mesh>
      {deltaSweepGeometry && (
        <mesh geometry={deltaSweepGeometry} renderOrder={1252}>
          <meshBasicMaterial
            color={ringColor}
            transparent
            opacity={RING_DELTA_SWEEP_OPACITY}
            depthTest={false}
            depthWrite={false}
            side={THREE.DoubleSide}
            toneMapped={false}
          />
        </mesh>
      )}
      {deltaSweepFillGeometry && (
        <mesh geometry={deltaSweepFillGeometry} renderOrder={1251}>
          <meshBasicMaterial
            color={ringColor}
            transparent
            opacity={RING_DELTA_SWEEP_FILL_OPACITY}
            depthTest={false}
            depthWrite={false}
            side={THREE.DoubleSide}
            toneMapped={false}
          />
        </mesh>
      )}
      <AxisArrow
        axis={axis}
        color={ringColor}
        isActive={isActive}
        angleDegrees={AXIS_ARROW_ANGLE_DEGREES + ringRotationDegrees}
      />
    </group>
  );
}

export default function PreferredAnglesManipulator({
  skeleton,
  selectedBoneIndex,
  enabled,
  manipulatorScale,
  poseVersion,
  propertiesVersion,
  onBonePropertiesChange,
  onDraggingChange,
}: PreferredAnglesManipulatorProps) {
  const cameraControls = useThree(
    (state) => state.controls,
  ) as CameraControlsImpl | null;
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);
  const size = useThree((state) => state.size);
  const rootRef = useRef<THREE.Group>(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const planeRef = useRef(new THREE.Plane());
  const intersectionRef = useRef(new THREE.Vector3());
  const localPointRef = useRef(new THREE.Vector3());
  const axisNormalRef = useRef(new THREE.Vector3());
  const ndcRef = useRef(new THREE.Vector2());
  const dragAnchorRef = useRef<DragAnchor | null>(null);

  const manipulatorStateRef = useRef<ManipulatorFrameState>({
    position: new THREE.Vector3(),
    scale: 1,
  });

  const [activeAxis, setActiveAxis] = useState<ManipulatorAxis>('x');
  const [dragState, setDragState] = useState<DragState | null>(null);

  const selectedBone = useMemo(() => {
    if (!enabled) return null;
    if (selectedBoneIndex === null) return null;
    if (selectedBoneIndex < 0 || selectedBoneIndex >= skeleton.bones.length) {
      return null;
    }
    return skeleton.bone(selectedBoneIndex);
  }, [enabled, selectedBoneIndex, skeleton]);

  const selectedBoneIdentity =
    selectedBone === null ? null : `${selectedBone.index}:${selectedBone.name}`;

  useEffect(() => {
    setActiveAxis('x');
    setDragState(null);
    dragAnchorRef.current = null;
  }, [selectedBoneIdentity]);

  const axisStates = useMemo<PreferredAnglesAxisState[]>(() => {
    if (!selectedBone) return [];
    const preferredAngles = readPreferredAnglesTriplet(selectedBone);
    return AXIS_ORDER.map((axis) => {
      const axisIndex = axisIndexFromKey(axis);
      return {
        axis,
        axisIndex,
        value: preferredAngles[axisIndex],
      };
    });
  }, [propertiesVersion, selectedBone]);

  const anchorData = useMemo<AnchorData | null>(() => {
    if (!selectedBone) return null;

    const position = new THREE.Vector3();
    const posedQuaternion = new THREE.Quaternion();
    selectedBone.posedWorld.decompose(
      position,
      posedQuaternion,
      new THREE.Vector3(),
    );

    const localMatrix = new THREE.Matrix4().multiplyMatrices(
      selectedBone.restLocal,
      selectedBone.poseLocal,
    );
    const { quaternion: localQuaternion } = decomposeMatrixToTransform(localMatrix);
    const gimbalQuats = buildGimbalQuaternions(
      posedQuaternion,
      localQuaternion,
      selectedBone.eulerOrder,
    );

    return {
      position,
      gimbalQuats: {
        x: gimbalQuats.x,
        y: gimbalQuats.y,
        z: gimbalQuats.z,
      },
    };
  }, [poseVersion, selectedBone]);

  const axisSweepPlacements = useMemo<
    Record<ManipulatorAxis, AxisSweepPlacement | null>
  >(() => {
    const empty: Record<ManipulatorAxis, AxisSweepPlacement | null> = {
      x: null,
      y: null,
      z: null,
    };
    if (!selectedBone || !anchorData) return empty;
    if (selectedBone.children.length === 0) return empty;

    const firstChildIndex = selectedBone.children[0];
    const childWorldPosition = skeleton.getBonePosedWorldPosition(firstChildIndex);
    const currentDirectionWorld = childWorldPosition
      .sub(anchorData.position.clone())
      .normalize();
    if (currentDirectionWorld.lengthSq() <= 1e-12) return empty;

    const preferredAngles = readPreferredAnglesTriplet(selectedBone);
    const selectedLocalMatrix = new THREE.Matrix4().multiplyMatrices(
      selectedBone.restLocal,
      selectedBone.poseLocal,
    );
    const { quaternion: currentLocalQuaternion } =
      decomposeMatrixToTransform(selectedLocalMatrix);
    const currentLocalEuler = eulerDegreesFromQuaternion(
      currentLocalQuaternion,
      selectedBone.eulerOrder,
    );
    const targetLocalEuler: [number, number, number] = [
      currentLocalEuler[0] + preferredAngles[0],
      currentLocalEuler[1] + preferredAngles[1],
      currentLocalEuler[2] + preferredAngles[2],
    ];
    const targetLocalQuaternion = quaternionFromEulerDegrees(
      targetLocalEuler,
      selectedBone.eulerOrder,
    );

    const parentWorldMatrix =
      selectedBone.parent === null
        ? skeleton.rootParentWorld
        : skeleton.bone(selectedBone.parent).posedWorld;
    const parentWorldQuaternion = new THREE.Quaternion();
    parentWorldMatrix.decompose(
      new THREE.Vector3(),
      parentWorldQuaternion,
      new THREE.Vector3(),
    );
    parentWorldQuaternion.normalize();

    const currentWorldQuaternion = parentWorldQuaternion
      .clone()
      .multiply(currentLocalQuaternion)
      .normalize();
    const targetWorldQuaternion = parentWorldQuaternion
      .clone()
      .multiply(targetLocalQuaternion)
      .normalize();
    const worldDeltaQuaternion = targetWorldQuaternion
      .clone()
      .multiply(currentWorldQuaternion.clone().invert())
      .normalize();
    const ghostDirectionWorld = currentDirectionWorld
      .clone()
      .applyQuaternion(worldDeltaQuaternion)
      .normalize();
    if (ghostDirectionWorld.lengthSq() <= 1e-12) return empty;

    const result: Record<ManipulatorAxis, AxisSweepPlacement | null> = {
      x: null,
      y: null,
      z: null,
    };

    for (const axis of AXIS_ORDER) {
      const axisQuaternion = anchorData.gimbalQuats[axis];
      const inverseAxisQuaternion = axisQuaternion.clone().invert();
      const currentLocalDirection = currentDirectionWorld
        .clone()
        .applyQuaternion(inverseAxisQuaternion);
      const ghostLocalDirection = ghostDirectionWorld
        .clone()
        .applyQuaternion(inverseAxisQuaternion);

      const startAngle = angleDegreesFromLocalPoint(axis, currentLocalDirection);
      const endAngle = angleDegreesFromLocalPoint(axis, ghostLocalDirection);
      const delta = normalizeDegrees(endAngle - startAngle);
      if (Math.abs(delta) < RING_DELTA_SWEEP_MIN_DEGREES) {
        result[axis] = null;
        continue;
      }

      if (delta >= 0) {
        result[axis] = {
          startDegrees: startAngle,
          endDegrees: startAngle + delta,
        };
      } else {
        result[axis] = {
          startDegrees: startAngle + delta,
          endDegrees: startAngle,
        };
      }
    }

    return result;
  }, [anchorData, poseVersion, propertiesVersion, selectedBone, skeleton]);

  const anchorPositionTuple = useMemo<[number, number, number] | null>(
    () =>
      anchorData
        ? [anchorData.position.x, anchorData.position.y, anchorData.position.z]
        : null,
    [anchorData],
  );

  const safeManipulatorScale = clampManipulatorScale(manipulatorScale);

  const setPreferredAngleValue = useCallback(
    (bone: Bone, axis: ManipulatorAxis, nextValue: number) => {
      const axisIndex = axisIndexFromKey(axis);
      const currentValues = readPreferredAnglesTriplet(bone);
      const normalizedNextValue =
        Math.abs(nextValue) < PREFERRED_ANGLE_EPSILON
          ? 0
          : normalizeDegrees(nextValue);
      if (
        Math.abs(currentValues[axisIndex] - normalizedNextValue) <
        PREFERRED_ANGLE_EPSILON
      ) {
        return;
      }

      const nextValues: [number, number, number] = [
        currentValues[0],
        currentValues[1],
        currentValues[2],
      ];
      nextValues[axisIndex] = normalizedNextValue;
      const hasSignificantValue = nextValues.some(
        (value) => Math.abs(value) >= PREFERRED_ANGLE_EPSILON,
      );

      if (!hasSignificantValue) {
        if (bone.properties?.preferredAngles) {
          bone.properties.preferredAngles = null;
          pruneBonePropertiesDefaults(bone);
          onBonePropertiesChange?.();
        }
        return;
      }

      const preferredAngles = ensurePreferredAngles(bone);
      preferredAngles.x = nextValues[0];
      preferredAngles.y = nextValues[1];
      preferredAngles.z = nextValues[2];
      pruneBonePropertiesDefaults(bone);
      onBonePropertiesChange?.();
    },
    [onBonePropertiesChange],
  );

  const handleRingPointerDown = useCallback(
    (axis: ManipulatorAxis, event: ThreeEvent<PointerEvent>) => {
      if (event.nativeEvent.button !== 0) return;
      if (!selectedBone || !anchorData) return;

      const axisQuaternion = anchorData.gimbalQuats[axis];
      const initialLocalPoint = event.point
        .clone()
        .sub(anchorData.position)
        .applyQuaternion(axisQuaternion.clone().invert())
        .divideScalar(Math.max(1e-5, manipulatorStateRef.current.scale));
      const initialAngle = angleDegreesFromLocalPoint(axis, initialLocalPoint);

      const axisIndex = axisIndexFromKey(axis);
      const preferredAngles = readPreferredAnglesTriplet(selectedBone);
      dragAnchorRef.current = {
        initialValue: preferredAngles[axisIndex],
        lastAngle: initialAngle,
        accumulatedDelta: 0,
      };

      setActiveAxis(axis);
      setDragState({ axis });
    },
    [anchorData, selectedBone],
  );

  useEffect(() => {
    if (!dragState) return;

    onDraggingChange?.(true);
    if (cameraControls) cameraControls.enabled = false;

    const handlePointerMove = (event: PointerEvent) => {
      if (!selectedBone || !anchorData) return;
      const dragAnchor = dragAnchorRef.current;
      if (!dragAnchor) return;

      const rect = gl.domElement.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      ndcRef.current.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      );

      const axisQuaternion = anchorData.gimbalQuats[dragState.axis];
      axisNormalRef.current
        .copy(axisNormalVector(dragState.axis))
        .applyQuaternion(axisQuaternion)
        .normalize();
      planeRef.current.setFromNormalAndCoplanarPoint(
        axisNormalRef.current,
        anchorData.position,
      );

      raycasterRef.current.setFromCamera(ndcRef.current, camera);
      const intersection = raycasterRef.current.ray.intersectPlane(
        planeRef.current,
        intersectionRef.current,
      );
      if (!intersection) return;

      localPointRef.current
        .copy(intersection)
        .sub(anchorData.position)
        .applyQuaternion(axisQuaternion.clone().invert())
        .divideScalar(Math.max(1e-5, manipulatorStateRef.current.scale));

      const nextAngle = angleDegreesFromLocalPoint(
        dragState.axis,
        localPointRef.current,
      );
      let frameDelta = nextAngle - dragAnchor.lastAngle;
      while (frameDelta > 180) frameDelta -= 360;
      while (frameDelta < -180) frameDelta += 360;
      dragAnchor.lastAngle = nextAngle;
      dragAnchor.accumulatedDelta += frameDelta;

      setPreferredAngleValue(
        selectedBone,
        dragState.axis,
        dragAnchor.initialValue + dragAnchor.accumulatedDelta,
      );
    };

    const endDrag = () => {
      setDragState(null);
      dragAnchorRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      onDraggingChange?.(false);
      if (cameraControls) cameraControls.enabled = true;
    };
  }, [
    anchorData,
    camera,
    cameraControls,
    dragState,
    gl,
    onDraggingChange,
    selectedBone,
    setPreferredAngleValue,
  ]);

  useEffect(() => {
    if (enabled && selectedBone) return;
    setDragState(null);
    dragAnchorRef.current = null;
  }, [enabled, selectedBone]);

  useFrame(() => {
    if (!enabled || !anchorData) return;

    const currentScale =
      computeTransformControlsScale(camera, anchorData.position) *
      safeManipulatorScale;
    manipulatorStateRef.current.position.copy(anchorData.position);
    manipulatorStateRef.current.scale = currentScale;

    const root = rootRef.current;
    if (!root) return;
    root.scale.setScalar(currentScale);
  });

  const ghostLineObjects = useMemo<GhostLineObject[]>(() => {
    if (!selectedBone) return [];
    if (!preferredAnglesAreSignificant(selectedBone.properties?.preferredAngles)) {
      return [];
    }

    const preferredAngles = readPreferredAnglesTriplet(selectedBone);
    const selectedLocalMatrix = new THREE.Matrix4().multiplyMatrices(
      selectedBone.restLocal,
      selectedBone.poseLocal,
    );
    const selectedLocalTransform = decomposeMatrixToTransform(selectedLocalMatrix);
    const selectedEuler = eulerDegreesFromQuaternion(
      selectedLocalTransform.quaternion,
      selectedBone.eulerOrder,
    );
    const targetEuler: [number, number, number] = [
      selectedEuler[0] + preferredAngles[0],
      selectedEuler[1] + preferredAngles[1],
      selectedEuler[2] + preferredAngles[2],
    ];
    const targetQuaternion = quaternionFromEulerDegrees(
      targetEuler,
      selectedBone.eulerOrder,
    );
    const selectedGhostLocalMatrix = composeMatrixFromTransform(
      selectedLocalTransform.position,
      targetQuaternion,
      selectedLocalTransform.scale,
    );
    const parentWorldMatrix =
      selectedBone.parent === null
        ? skeleton.rootParentWorld
        : skeleton.bone(selectedBone.parent).posedWorld;
    const selectedGhostWorldMatrix = new THREE.Matrix4().multiplyMatrices(
      parentWorldMatrix,
      selectedGhostLocalMatrix,
    );

    const ghostWorldByIndex = new Map<number, THREE.Matrix4>();
    ghostWorldByIndex.set(selectedBone.index, selectedGhostWorldMatrix);

    const buildQueue = [...selectedBone.children];
    while (buildQueue.length > 0) {
      const boneIndex = buildQueue.shift();
      if (boneIndex === undefined) continue;
      const bone = skeleton.bone(boneIndex);
      if (bone.parent === null) continue;
      const parentGhostWorld = ghostWorldByIndex.get(bone.parent);
      if (!parentGhostWorld) continue;

      const boneLocalMatrix = new THREE.Matrix4().multiplyMatrices(
        bone.restLocal,
        bone.poseLocal,
      );
      const boneGhostWorld = new THREE.Matrix4().multiplyMatrices(
        parentGhostWorld,
        boneLocalMatrix,
      );
      ghostWorldByIndex.set(boneIndex, boneGhostWorld);
      for (const childIndex of bone.children) {
        buildQueue.push(childIndex);
      }
    }

    const edges: Array<{ start: THREE.Vector3; end: THREE.Vector3 }> = [];
    const traversal = [selectedBone.index];
    const parentPosition = new THREE.Vector3();
    const childPosition = new THREE.Vector3();
    let subtreeExtent = 0;

    while (traversal.length > 0) {
      const parentIndex = traversal.pop();
      if (parentIndex === undefined) continue;
      const parentGhostWorld = ghostWorldByIndex.get(parentIndex);
      if (!parentGhostWorld) continue;

      parentPosition.setFromMatrixPosition(parentGhostWorld);
      const parentBone = skeleton.bone(parentIndex);
      for (const childIndex of parentBone.children) {
        const childGhostWorld = ghostWorldByIndex.get(childIndex);
        if (!childGhostWorld) continue;
        childPosition.setFromMatrixPosition(childGhostWorld);
        subtreeExtent = Math.max(subtreeExtent, parentPosition.distanceTo(childPosition));
        edges.push({
          start: parentPosition.clone(),
          end: childPosition.clone(),
        });
        traversal.push(childIndex);
      }
    }

    if (edges.length === 0) return [];

    const { dashLength, gapLength } = resolveGhostDashMetrics(subtreeExtent);
    const positions: number[] = [];
    const colors: number[] = [];
    let startsWhite = true;
    for (const edge of edges) {
      startsWhite = appendAlternatingDashedSegment({
        positions,
        colors,
        start: edge.start,
        end: edge.end,
        dashLength,
        gapLength,
        startsWhite,
      });
    }

    if (positions.length === 0) return [];

    const whitePositions: number[] = [];
    const blackPositions: number[] = [];
    for (let i = 0; i < positions.length; i += 6) {
      const isWhite = colors[i] > 0.5;
      const target = isWhite ? whitePositions : blackPositions;
      target.push(
        positions[i],
        positions[i + 1],
        positions[i + 2],
        positions[i + 3],
        positions[i + 4],
        positions[i + 5],
      );
    }

    const buildGhostLineObject = (
      key: 'white' | 'black',
      color: number,
      linePositions: number[],
    ): GhostLineObject | null => {
      if (linePositions.length === 0) return null;
      const geometry = new LineSegmentsGeometry();
      geometry.setPositions(linePositions);
      const material = new LineMaterial({
        color,
        linewidth: GHOST_LINE_WIDTH,
        transparent: GHOST_LINE_OPACITY < 1,
        opacity: GHOST_LINE_OPACITY,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
      });
      material.resolution.set(size.width, size.height);
      const line = new LineSegments2(geometry, material);
      line.renderOrder = GHOST_RENDER_ORDER;
      line.frustumCulled = false;
      return { key, geometry, material, line };
    };

    return [
      buildGhostLineObject('white', 0xffffff, whitePositions),
      buildGhostLineObject('black', 0x000000, blackPositions),
    ].filter((value): value is GhostLineObject => value !== null);
  }, [
    poseVersion,
    propertiesVersion,
    selectedBone,
    skeleton,
    GHOST_LINE_OPACITY,
    GHOST_LINE_WIDTH,
    size.height,
    size.width,
  ]);

  useEffect(
    () => () => {
      for (const { geometry, material } of ghostLineObjects) {
        geometry.dispose();
        material.dispose();
      }
    },
    [ghostLineObjects],
  );

  useEffect(() => {
    for (const { material } of ghostLineObjects) {
      material.resolution.set(size.width, size.height);
    }
  }, [ghostLineObjects, size.height, size.width]);

  if (!enabled || !selectedBone || !anchorData || !anchorPositionTuple) {
    return null;
  }

  return (
    <>
      {ghostLineObjects.map(({ key, line }) => (
        <primitive key={`ghost-line-${key}`} object={line} raycast={() => undefined} />
      ))}
      <group ref={rootRef} position={anchorPositionTuple}>
        {axisStates.map((axisState) => (
          <PreferredAnglesAxisRing
            key={axisState.axis}
            axis={axisState.axis}
            axisQuaternion={anchorData.gimbalQuats[axisState.axis]}
            isActive={axisState.axis === activeAxis}
            deltaDegrees={axisState.value}
            sweepPlacement={axisSweepPlacements[axisState.axis]}
            manipulatorStateRef={manipulatorStateRef}
            onPointerDown={handleRingPointerDown}
          />
        ))}
      </group>
    </>
  );
}
